import React from 'react';
import clsx from 'clsx';

import { NavItem } from '.';
import { CloseIcon } from './CloseIcon';
import { BurgerIcon } from './BurgerIcon';
import { MobileMenu } from './MobileMenu';
import { useAppContext, useLocalize } from '../../hooks';
import { Avatar, Clock, Link, LanguageSwitch } from '../../components';
import type { MenuItem } from './MainLayout';

type HeaderProps = {
  logo?: React.ReactNode;
  links?: MenuItem[];
  email?: string;
  isMobile: boolean;
  avatarUrl?: string;
  userName?: string;
  className?: string;
  linkClassName?: string;
};

const renderNavList = (
  links: MenuItem[],
  linkClassName?: string
): React.ReactNode => (
  <nav className="hidden md:flex xs:gap-x-0 md:gap-x-0 lg:gap-x-md xl:gap-x-lg md:pl-md">
    {links
      .filter(l => l.text)
      .map(link => {
        return (
          <NavItem
            key={link.text || link.url}
            menuItem={link}
            linkClassName={clsx(
              linkClassName,
              'hover:text-main transition px-md font-semibold'
            )}
          />
        );
      })}
  </nav>
);

export const Header: React.FC<HeaderProps> = ({
  logo = '',
  isMobile,
  links = [],
  userName = '',
  avatarUrl = '',
  linkClassName = '',
  className = 'fixed top-0 left-0 right-0',
}) => {
  const { t } = useLocalize();
  const { menuOpened, toggleMenu } = useAppContext();

  return (
    <header
      className={clsx(
        'flex items-center justify-between py-md px-[2rem] sm:px-2xl',
        className,
        !isMobile ? 'shadow-md' : ''
      )}
    >
      <div className="flex items-center grow">
        <Link url="/">{logo}</Link>
        {!isMobile ? renderNavList(links, linkClassName) : null}
      </div>
      <div className="flex items-center gap-x-xs sm:gap-x-md">
        <LanguageSwitch />
        <Clock
          formatType="h[h] mm[m]"
          timeClassName="text-small font-semibold"
          time={new Date(Date.now() + 50 * 600000)}
          label={!isMobile ? t('menu.hoursleft') : ''}
          className="text-accordionBorder px-md font-semibold"
        />
        {isMobile ? (
          !menuOpened ? (
            <BurgerIcon onClick={toggleMenu} />
          ) : (
            <CloseIcon onClick={toggleMenu} />
          )
        ) : (
          <Avatar
            userName={userName}
            avatarUrl={avatarUrl}
            className="w-avatar h-avatar"
          />
        )}
        {!!isMobile && (
          <MobileMenu links={links} userName={userName} avatarUrl={avatarUrl} />
        )}
      </div>
    </header>
  );
};
